import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {NestedTreeControl} from "@angular/cdk/tree";
import {CategoryNode} from "../../../dtos/vocab/category/category-node";
import {MatTreeNestedDataSource} from "@angular/material/tree";
import {SystemicGrammarCategoryDto} from "../../../dtos/grammar/sys/systemic-grammar-category-dto";
import {FuncGrammarCategoryDto} from "../../../dtos/grammar/func/func-grammar-category-dto";
import {CategoryService} from "../../../services/vocab/category.service";
import {FuncGrammarService} from "../../../services/grammar/func-grammar.service";
import {SysGrammarService} from "../../../services/grammar/sys-grammar.service";
import {VerbalActionCategoryService} from "../../../services/verbal-action/verbal-action-category.service";
import {GlobalCanDoStatementCategoryService} from "../../../services/can-do-statement/global-can-do-statement-category.service";
import {DetailedCanDoStatementCategoryService} from "../../../services/can-do-statement/detailed-can-do-statement-category.service";
import {NewGrammarCategoryComponent} from "../../grammar/new-grammar-category/new-grammar-category.component";
import {NewVerbalActionCategoryComponent} from "../../verbal-action/create/new-category/new-verbal-action-category.component";
import {NewCanDoStatementCategoryComponent} from "../../can-do-statement/create/new-category/new-can-do-statement-category.component";
import {MatDialog} from "@angular/material/dialog";
import {NotificationService} from "../../../services/common/notification.service";
import {ConfirmationDialogComponent} from "../confirmation-dialog/confirmation-dialog.component";

export interface EventData {
  categoryControl: number,
  resetPageIndex: boolean;
}

@Component({
  selector: 'app-category-tree',
  templateUrl: './category-tree.component.html',
  styleUrl: './category-tree.component.scss',
  standalone: false
})
export class CategoryTreeComponent implements OnChanges {

  @Input() displayEditingTools: boolean
  @Input() service: CategoryService | SysGrammarService | FuncGrammarService | VerbalActionCategoryService
    | GlobalCanDoStatementCategoryService | DetailedCanDoStatementCategoryService;
  @Input() isAdmin: boolean;
  @Input() reloadRootsTrigger: boolean;
  @Input() resetCategoryControl: boolean;
  @Input() categoryId: number;
  @Input() title: string = 'Themen';

  @Output() reloadSearch = new EventEmitter<EventData>();

  // category tree
  treeData: CategoryNode[] | SystemicGrammarCategoryDto[] | FuncGrammarCategoryDto[];
  treeControl = new NestedTreeControl<CategoryNode | SystemicGrammarCategoryDto | FuncGrammarCategoryDto>(node => node.subcategories);
  dataSource = new MatTreeNestedDataSource<CategoryNode | SystemicGrammarCategoryDto | FuncGrammarCategoryDto>();

  categoryControl: number;

  constructor(private dialog: MatDialog, private notificationService: NotificationService) {
    this.dataSource.data = this.treeData;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['reloadRootsTrigger']) {
      // also covers the initial load, where both inputs change together
      this.getRoots(changes['reloadRootsTrigger'].firstChange);
    } else if (changes['categoryId'] && this.categoryId) {
      // same-route deep link: categoryId alone changed - select it against the loaded tree
      this.categoryId = Number(this.categoryId);
      this.expandToId(this.treeControl, this.treeData, this.categoryId);
      this.setCategoryID(this.categoryId);
      this.categoryId = null;
    }
    if (changes['resetCategoryControl']) {
      if (this.categoryControl !== null)
        this.setCategoryID(this.categoryControl, false)
    }
  }

  getRoots(initial: boolean = false) {
    this.service.getRootCategories().subscribe(
      roots => {
        this.treeData = roots;
        this.dataSource.data = this.treeData;
        if (!initial) {
          this.setCategoryID(null);
        }
        if (this.categoryId) {
          this.categoryId = Number(this.categoryId)
          this.expandToId(this.treeControl, this.treeData, this.categoryId)
          this.setCategoryID(this.categoryId);
          this.categoryId = null;
        }
      }, error => {
        this.notificationService.openSnackBar(error);
      }
    );
  }


  /**
   * set current category
   * if the current category is selected, select null
   *
   * @param id of the current category
   * @param reload defines if the search should be reloaded
   */
  setCategoryID(id: number, reload: boolean = true) {
    if (this.categoryControl === id) {
      this.categoryControl = null;
    } else {
      this.categoryControl = id;
    }
    if (reload) {
      this.reloadSearch.emit({categoryControl: this.categoryControl, resetPageIndex: true})
    }
  }

  editCategory($event: number) {
    let component: any;
    if (this.service instanceof VerbalActionCategoryService) {
      component = NewVerbalActionCategoryComponent;
    } else if (this.service instanceof GlobalCanDoStatementCategoryService || this.service instanceof DetailedCanDoStatementCategoryService) {
      component = NewCanDoStatementCategoryComponent;
    } else {
      component = NewGrammarCategoryComponent;
    }
    const dialogRef = this.dialog.open(component, {
      data: {
        id: $event,
        service: this.service
      }
    });
    dialogRef.afterClosed().subscribe({
      next: result => {
        if (result) {
          this.getRoots();
          this.reloadSearch.emit({categoryControl: this.categoryControl, resetPageIndex: true})
        }
      }, error: (error) => {
        this.notificationService.openSnackBar(error);
      }
    });
  }

  deleteCategory($event: CategoryNode) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        headline: 'Thema löschen',
        content: 'Das Thema "' + $event.name + '" und ihre Unterthemen werden unwiderruflich gelöscht und können unter keinen Umständen ' +
          'wiederhergestellt werden. \n  Wollen Sie die Thema wirklich löschen?',
        action: 'Löschen'
      }
    });
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.service.deleteCategory($event.id).subscribe({
          next: () => {
            this.getRoots();
            this.reloadSearch.emit({categoryControl: this.categoryControl, resetPageIndex: true})
            this.notificationService.openSnackBar('Thema ' + $event.name + ' wurde gelöscht.');
          }, error: error => {
            this.notificationService.openSnackBar(error);
          },
        });
      }
    });
  }

  expandToId(treeControl: NestedTreeControl<CategoryNode | SystemicGrammarCategoryDto | FuncGrammarCategoryDto>, data: CategoryNode[] | SystemicGrammarCategoryDto[] | FuncGrammarCategoryDto[], id: number) {
    const path = this.findPathToId(data, id);
    if (path) {
      // expand all except the last (the target itself)
      path.slice(0, -1).forEach(node => treeControl.expand(node));
    }
  }


  findPathToId(tree: CategoryNode[] | SystemicGrammarCategoryDto[] | FuncGrammarCategoryDto[], targetId: number): any {
    for (const node of tree) {
      if (node.id === targetId) {
        return [node];
      }
      if (node.subcategories?.length) {
        const path = this.findPathToId(node.subcategories, targetId);
        if (path) {
          return [node, ...path];
        }
      }
    }
    return null;
  }

  /**
   * checks if this node has children
   *
   * @param _ needed for style
   * @param node of tree
   */
  hasChild = (_: number, node: CategoryNode) => !!node.subcategories && node.subcategories.length > 0;

}
